import { z } from "zod";
/**
 * Subscription tier schema
 */
export const subscriptionTierSchema = z.enum([
    "free",
    "starter",
    "pro",
    "byok",
]);
/**
 * Subscription status schema
 */
export const subscriptionStatusSchema = z.enum([
    "active",
    "past_due",
    "canceled",
    "trialing",
    "grace_period",
]);
/**
 * Billing interval schema
 */
export const billingIntervalSchema = z.enum(["month", "year"]);
/**
 * Subscription input schema (for creation/updates)
 */
export const subscriptionInputSchema = z.object({
    userId: z.string().min(1),
    tier: subscriptionTierSchema,
    status: subscriptionStatusSchema,
    stripeCustomerId: z.string().nullable().optional(),
    stripeSubscriptionId: z.string().nullable().optional(),
    stripePriceId: z.string().nullable().optional(),
    billingInterval: billingIntervalSchema.nullable().optional(),
    currentPeriodStart: z.string().datetime().optional(),
    currentPeriodEnd: z.string().datetime().optional(),
    cancelAtPeriodEnd: z.boolean().default(false),
    tokensPerMonth: z.number().int().nonnegative(),
});
/**
 * Subscription schema for database storage
 */
export const subscriptionSchema = subscriptionInputSchema.extend({
    id: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * Token balance input schema
 */
export const tokenBalanceInputSchema = z.object({
    userId: z.string().min(1),
    subscriptionTokens: z.number().int().nonnegative(),
    topUpTokens: z.number().int().nonnegative().default(0),
    monthlyAllocation: z.number().int().nonnegative(),
    lastResetAt: z.string().datetime(),
});
/**
 * Token balance schema
 */
export const tokenBalanceSchema = tokenBalanceInputSchema.extend({
    updatedAt: z.string().datetime(),
});
/**
 * Token usage tracking schema
 */
export const tokenUsageSchema = z.object({
    id: z.string(),
    userId: z.string().min(1),
    tokensUsed: z.number().int().positive(),
    source: z.enum(["subscription", "topup"]),
    model: z.string(),
    conversationId: z.string().optional(),
    createdAt: z.string().datetime(),
});
/**
 * BYOK (Bring Your Own Key) input schema
 */
export const byokKeyInputSchema = z.object({
    userId: z.string().min(1),
    provider: z.enum(["anthropic", "openai"]),
    encryptedKey: z.string().min(1),
    keyHint: z.string().max(8), // Last 4 chars for display
});
/**
 * BYOK key schema with timestamps
 */
export const byokKeySchema = byokKeyInputSchema.extend({
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * Tier configuration schema
 */
export const tierConfigSchema = z.object({
    tier: subscriptionTierSchema,
    name: z.string(),
    monthlyPrice: z.number().int().min(0), // cents
    yearlyPrice: z.number().int().min(0), // cents
    tokenAllocation: z.number().int().min(0),
    features: z.array(z.string()),
});
/**
 * Tier configuration constant matching PROJECT.md pricing
 */
export const TIER_CONFIG = {
    free: {
        tier: "free",
        name: "Free",
        monthlyPrice: 0,
        yearlyPrice: 0,
        tokenAllocation: 50000,
        features: [
            "50K tokens/month",
            "Basic task management",
            "Email reminders",
            "Mobile PWA access",
        ],
    },
    starter: {
        tier: "starter",
        name: "Starter",
        monthlyPrice: 999, // $9.99
        yearlyPrice: 9990, // $99.90 (2 months free)
        tokenAllocation: 500000,
        features: [
            "500K tokens/month",
            "Voice input",
            "Calendar sync",
            "Priority support",
        ],
    },
    pro: {
        tier: "pro",
        name: "Pro",
        monthlyPrice: 2999, // $29.99
        yearlyPrice: 29990, // $299.90 (2 months free)
        tokenAllocation: 2000000,
        features: [
            "2M tokens/month",
            "Everything in Starter",
            "Team sharing",
            "API access",
            "Custom integrations",
        ],
    },
    byok: {
        tier: "byok",
        name: "Bring Your Own Key",
        monthlyPrice: 1900, // $19.00
        yearlyPrice: 19000, // $190.00 (2 months free)
        tokenAllocation: 0, // Unlimited, uses user's API key
        features: [
            "Unlimited usage",
            "Use your own API key",
            "All Pro features",
            "Key encryption at rest",
        ],
    },
};
//# sourceMappingURL=subscription.js.map